

const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    'audio[controls]',
    'video[controls]',
    'details',
    'summary',
    'iframe',
    'object',
    'embed',
    '[contenteditable=""]',
    '[contenteditable="true"]',
].join(',');

const SCROLLABLE_MARK_ATTR = 'data-scrollable-region-fix';

function isScrollable(el: HTMLElement): boolean {
    const style = window.getComputedStyle(el);
    const yScroll = style.overflowY === 'auto' || style.overflowY === 'scroll';
    const xScroll = style.overflowX === 'auto' || style.overflowX === 'scroll';
    if (!yScroll && !xScroll) {
        return false;
    }
    if (yScroll && el.scrollHeight - el.clientHeight > 1) {
        return true;
    }
    if (xScroll && el.scrollWidth - el.clientWidth > 1) {
        return true;
    }
    return false;
}

function ensureFocusable(): void {
    // querySelectorAll('*') is O(n) and acceptable here because the scanner
    // is debounced and only runs after the user-perceptible work is done.
    const all = document.body.querySelectorAll<HTMLElement>('*');
    all.forEach((el) => {
        if (!isScrollable(el)) {
            // Drop our marker if the element is no longer scrollable so we
            // don't leak stale tabindexes (e.g. after content collapses).
            if (el.getAttribute(SCROLLABLE_MARK_ATTR) === 'true') {
                el.removeAttribute('tabindex');
                el.removeAttribute(SCROLLABLE_MARK_ATTR);
            }
            return;
        }
        if (el.hasAttribute('tabindex')) {
            return;
        }
        if (el.matches(FOCUSABLE_SELECTOR)) {
            return;
        }
        if (el.querySelector(FOCUSABLE_SELECTOR)) {
            return;
        }
        el.setAttribute('tabindex', '0');
        el.setAttribute(SCROLLABLE_MARK_ATTR, 'true');
        // Give the region a generic accessible name only when it has none.
        // Most apps name their scroll panels naturally via headings inside;
        // we only label as a last resort so screen readers can announce
        // "scrollable region" instead of an empty group on focus.
        if (!el.hasAttribute('aria-label') && !el.hasAttribute('aria-labelledby')) {
            el.setAttribute('aria-label', 'Scrollable content region');
        }
    });
}

export function initializeScrollableRegionAccessibility(): MutationObserver | null {
    if (typeof document === 'undefined') {
        return null;
    }

    let scheduled = false;
    const schedule = () => {
        if (scheduled) return;
        scheduled = true;
        // requestAnimationFrame yields one frame so React commits, layout,
        // and paint complete before we measure scrollHeight/clientHeight.
        requestAnimationFrame(() => {
            scheduled = false;
            ensureFocusable();
        });
    };

    const observer = new MutationObserver(schedule);
    observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['hidden'],
    });

    // Re-run on resize so containers that become scrollable at narrower
    // viewports also get keyboard access.
    window.addEventListener('resize', schedule, { passive: true });

    if (document.readyState !== 'loading') {
        schedule();
    } else {
        document.addEventListener('DOMContentLoaded', schedule, { once: true });
    }

    return observer;
}
